// 프로그램관리 화면 (관리자 전용) - 메뉴 마스터 CRUD
import React, { useState, useEffect, useCallback } from 'react';
import PageLayout from '../../components/PageLayout';
import { getManageMenus, createMenu, updateMenu, deleteMenu } from '../../api/menu';
import './MenuMasterPage.css';

const fmtDt = (dt) => {
  if (!dt) return '-';
  const d = new Date(dt);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const EMPTY_NEW = {
  menu_code: '', menu_name: '', group_code: '', group_name: '',
  sort_order: 99, is_admin: false, is_active: true,
};

const MenuMasterPage = ({ isActive }) => {
  const [menus,    setMenus]    = useState([]);
  const [message,  setMessage]  = useState({ text: '', type: '' });
  const [editId,   setEditId]   = useState(null);   // 수정 중인 메뉴 id
  const [editRow,  setEditRow]  = useState({});     // 수정 임시값
  const [addRow,   setAddRow]   = useState(null);   // 신규 행 (null=미표시)
  const [busy,     setBusy]     = useState(false);

  const showMsg = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  }, []);

  const loadMenus = useCallback(async () => {
    try {
      const res = await getManageMenus();
      setMenus(res.menus || []);
    } catch {
      showMsg('메뉴 목록 조회 실패', 'error');
    }
  }, [showMsg]);

  useEffect(() => {
    if (!isActive) return;
    loadMenus();
  }, [isActive, loadMenus]);

  // ── 편집 시작 ──
  const startEdit = (m) => {
    setEditId(m.id);
    setEditRow({ ...m });
    setAddRow(null);
  };

  // ── 편집 취소 ──
  const cancelEdit = () => { setEditId(null); setEditRow({}); };

  // ── 편집 저장 ──
  const saveEdit = async () => {
    if (!editRow.menu_name?.trim()) { showMsg('화면명을 입력하세요.', 'error'); return; }
    if (!editRow.group_code?.trim()) { showMsg('그룹코드를 입력하세요.', 'error'); return; }
    if (!editRow.group_name?.trim()) { showMsg('그룹명을 입력하세요.', 'error'); return; }
    setBusy(true);
    try {
      await updateMenu(editId, {
        menu_name:  editRow.menu_name,
        group_code: editRow.group_code,
        group_name: editRow.group_name,
        sort_order: Number(editRow.sort_order) || 99,
        is_admin:   editRow.is_admin,
        is_active:  editRow.is_active,
      });
      showMsg('수정되었습니다.');
      cancelEdit();
      await loadMenus();
    } catch (e) {
      showMsg(e.response?.data?.detail || '수정 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── 삭제 ──
  const handleDelete = async (m) => {
    if (!window.confirm(`'${m.menu_name}' 메뉴를 삭제하시겠습니까?\n관련 권한 데이터도 함께 삭제됩니다.`)) return;
    setBusy(true);
    try {
      await deleteMenu(m.id);
      showMsg('삭제되었습니다.');
      await loadMenus();
    } catch (e) {
      showMsg(e.response?.data?.detail || '삭제 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── 신규 행 추가 ──
  const startAdd = () => {
    setAddRow({ ...EMPTY_NEW });
    setEditId(null);
    setEditRow({});
  };

  const cancelAdd = () => setAddRow(null);

  const saveAdd = async () => {
    if (!addRow.menu_code?.trim())  { showMsg('메뉴코드를 입력하세요.', 'error'); return; }
    if (!addRow.menu_name?.trim())  { showMsg('화면명을 입력하세요.', 'error'); return; }
    if (!addRow.group_code?.trim()) { showMsg('그룹코드를 입력하세요.', 'error'); return; }
    if (!addRow.group_name?.trim()) { showMsg('그룹명을 입력하세요.', 'error'); return; }
    setBusy(true);
    try {
      await createMenu({
        menu_code:  addRow.menu_code.trim(),
        menu_name:  addRow.menu_name.trim(),
        group_code: addRow.group_code.trim(),
        group_name: addRow.group_name.trim(),
        sort_order: Number(addRow.sort_order) || 99,
        is_admin:   addRow.is_admin,
        is_active:  addRow.is_active,
      });
      showMsg('메뉴가 추가되었습니다.');
      cancelAdd();
      await loadMenus();
    } catch (e) {
      showMsg(e.response?.data?.detail || '추가 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  const setAdd = (k, v) => setAddRow(p => ({ ...p, [k]: v }));
  const setEdit = (k, v) => setEditRow(p => ({ ...p, [k]: v }));

  return (
    <PageLayout title="프로그램관리" message={message}>
      <div className="mm-wrap">

        {/* ── 툴바 ── */}
        <div className="mm-toolbar">
          <button className="btn btn-sm btn-primary" onClick={startAdd} disabled={!!addRow || busy}>
            + 메뉴 추가
          </button>
          <button className="btn btn-sm btn-outline" onClick={loadMenus} disabled={busy}>
            새로고침
          </button>
          <span style={{ fontSize: 12, color: '#888' }}>총 {menus.length}개</span>
        </div>

        {/* ── 테이블 ── */}
        <div className="mm-table-wrap">
          <table className="mm-table">
            <thead>
              <tr>
                <th className="mm-col-code">메뉴코드</th>
                <th className="mm-col-name">화면명</th>
                <th className="mm-col-grp">그룹코드</th>
                <th className="mm-col-grpnm">그룹명</th>
                <th className="mm-col-sort">순서</th>
                <th className="mm-col-admin">관리자</th>
                <th className="mm-col-active">활성</th>
                <th className="mm-col-date">등록일</th>
                <th className="mm-col-action">관리</th>
              </tr>
            </thead>
            <tbody>

              {/* ── 신규 입력 행 ── */}
              {addRow && (
                <tr className="mm-row-new">
                  <td><input className="mm-input" value={addRow.menu_code}
                    onChange={e => setAdd('menu_code', e.target.value)} placeholder="영문/숫자" autoFocus /></td>
                  <td><input className="mm-input" value={addRow.menu_name}
                    onChange={e => setAdd('menu_name', e.target.value)} placeholder="화면명" /></td>
                  <td>
                    <select className="mm-select" value={addRow.group_code}
                      onChange={e => {
                        const gc = e.target.value;
                        const gn = gc === 'reference' ? '기준정보'
                                 : gc === 'trade'     ? '거래'
                                 : gc === 'status'    ? '현황'
                                 : gc === 'admin'     ? '관리자' : '';
                        setAddRow(p => ({ ...p, group_code: gc, group_name: gn }));
                      }}>
                      <option value="">선택</option>
                      <option value="reference">reference</option>
                      <option value="trade">trade</option>
                      <option value="status">status</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td><input className="mm-input" value={addRow.group_name}
                    onChange={e => setAdd('group_name', e.target.value)} placeholder="그룹명" /></td>
                  <td><input className="mm-input mm-input-sm" type="number" value={addRow.sort_order}
                    onChange={e => setAdd('sort_order', e.target.value)} /></td>
                  <td className="mm-td-center">
                    <input type="checkbox" checked={addRow.is_admin}
                      onChange={e => setAdd('is_admin', e.target.checked)} />
                  </td>
                  <td className="mm-td-center">
                    <input type="checkbox" checked={addRow.is_active}
                      onChange={e => setAdd('is_active', e.target.checked)} />
                  </td>
                  <td>-</td>
                  <td className="mm-td-center">
                    <button className="btn btn-sm btn-primary" onClick={saveAdd} disabled={busy}>저장</button>
                    {' '}
                    <button className="btn btn-sm btn-outline" onClick={cancelAdd} disabled={busy}>취소</button>
                  </td>
                </tr>
              )}

              {/* ── 기존 행 ── */}
              {menus.map(m => {
                const isEditing = editId === m.id;
                return (
                  <tr key={m.id}>
                    <td title={m.menu_code}>{m.menu_code}</td>
                    <td>
                      {isEditing
                        ? <input className="mm-input" value={editRow.menu_name}
                            onChange={e => setEdit('menu_name', e.target.value)} autoFocus />
                        : m.menu_name}
                    </td>
                    <td>
                      {isEditing
                        ? <select className="mm-select" value={editRow.group_code}
                            onChange={e => {
                              const gc = e.target.value;
                              const gn = gc === 'reference' ? '기준정보'
                                       : gc === 'trade'     ? '거래'
                                       : gc === 'status'    ? '현황'
                                       : gc === 'admin'     ? '관리자' : editRow.group_name;
                              setEditRow(p => ({ ...p, group_code: gc, group_name: gn }));
                            }}>
                            <option value="reference">reference</option>
                            <option value="trade">trade</option>
                            <option value="status">status</option>
                            <option value="admin">admin</option>
                          </select>
                        : m.group_code}
                    </td>
                    <td>
                      {isEditing
                        ? <input className="mm-input" value={editRow.group_name}
                            onChange={e => setEdit('group_name', e.target.value)} />
                        : m.group_name}
                    </td>
                    <td className="mm-td-sort">
                      {isEditing
                        ? <input className="mm-input mm-input-sm" type="number" value={editRow.sort_order}
                            onChange={e => setEdit('sort_order', e.target.value)} />
                        : m.sort_order}
                    </td>
                    <td className="mm-td-center">
                      {isEditing
                        ? <input type="checkbox" checked={editRow.is_admin}
                            onChange={e => setEdit('is_admin', e.target.checked)} />
                        : m.is_admin
                          ? <span className="mm-badge-admin">관리자</span>
                          : <span className="mm-badge-user">일반</span>}
                    </td>
                    <td className="mm-td-center">
                      {isEditing
                        ? <input type="checkbox" checked={editRow.is_active}
                            onChange={e => setEdit('is_active', e.target.checked)} />
                        : m.is_active
                          ? <span className="mm-badge-active">활성</span>
                          : <span className="mm-badge-off">비활성</span>}
                    </td>
                    <td>{fmtDt(m.created_at)}</td>
                    <td className="mm-td-center">
                      {isEditing ? (
                        <>
                          <button className="btn btn-sm btn-primary" onClick={saveEdit} disabled={busy}>저장</button>
                          {' '}
                          <button className="btn btn-sm btn-outline" onClick={cancelEdit} disabled={busy}>취소</button>
                        </>
                      ) : (
                        <>
                          <button className="btn-link" onClick={() => startEdit(m)} disabled={busy}>수정</button>
                          {' · '}
                          <button className="btn-link" style={{ color: '#e74c3c' }}
                            onClick={() => handleDelete(m)} disabled={busy}>삭제</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}

              {menus.length === 0 && !addRow && (
                <tr><td colSpan={9} className="mm-empty">등록된 메뉴가 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageLayout>
  );
};

export default MenuMasterPage;
